import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FontAwesome from 'Components/UI/FontAwesome/FontAwesome';
import { Grid, useTheme } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import useMobileLayout from 'Hooks/useMobileLayout';
import HoverMenu
  from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/ThreadList/Thread/Body/Shared/Message/Shared/MessageImages/HoverMenu';
import { pickFunction } from 'Helpers/Affordances';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import { iconMapper } from 'Components/Files/utils';
import useAttachment from './useAttachment';

const styles = theme => ({
  root: {
    position: 'relative',
    width: 240,
    minWidth: 240,
    maxHeight: 66,
    margin: theme.spacing(1),
    '&:hover > .hoverMenuContainer': {
      visibility: 'visible',
    },
  },
  rootDesktop: {
    padding: theme.spacing(1.5),
  },
  rootMobile: {
    padding: theme.spacing(0.5, 1),
  },
  content: {
    cursor: 'pointer',
    '&:hover': {
      '& $downloadIcon': {
        transform: 'unset',
        opacity: 1, // slide and show downloadIcon class on hover
      },
      '& $fileName': {
        textDecoration: 'underline', // underline fileName class on hover
      },
      '& $downloadText': {
        opacity: 1,
        transform: 'unset', // slide and show downloadText class on hover
      },
      '& $fileLabel': {
        opacity: 0, // hide fileLabel class on hover
      },
    },
  },
  fileIcon: {
    position: 'relative',
  },
  labelBlockMobile: {
    paddingLeft: `${theme.spacing(0.5)}px !important`,
  },
  fileName: {
    fontWeight: 500,
  },
  fileNameMobile: {
    fontSize: 14,
  },
  fileLabel: {
    opacity: 1,
    transition: '250ms ease-in-out',
  },
  downloadText: {
    opacity: 0,
    position: 'absolute',
    transform: 'translateY(-5px)',
    transition: '250ms ease-in-out',
  },
  labelFontMobile: {
    fontSize: 12,
  },
  downloadIcon: {
    opacity: 0,
    position: 'absolute',
    bottom: '15%',
    right: '25%',
    backgroundColor: 'white',
    borderRadius: '100%',
    transform: 'translateY(-5px)',
    transition: '250ms ease-in-out',
  },
  downloadIconDesktop: {
    paddingTop: 1,
    border: '3px solid white',
  },
  downloadIconMobile: {
    border: '1px solid white',
    marginRight: '-10%',
  },
  deleteIconButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translateX(50%) translateY(-50%)',
    cursor: 'pointer',
  },
  deleteIcon: {
    backgroundColor: 'white',
  },
});


const MessageAttachment = ({
  className,
  classes,
  attachmentResource,
  onClickOverride,
  onDelete,
  showHoverMenu,
}) => {
  const { userResource } = useUserContext();
  const isMobile = useMobileLayout();
  const theme = useTheme();
  const {
    rootNodeProps,
    onClick,
    onDownload,
    onSendToSecureFiles,
    sendingToSecureFiles,
  } = useAttachment(attachmentResource);

  const {
    icon,
    color,
    label,
  } = iconMapper(attachmentResource.attr('name').split('.').pop().toLowerCase(), theme);

  const hoverMenu = pickFunction(userResource, {
    [fr.relationships.default.name]: () => <HoverMenu onDownload={onDownload} onSendToSecureFiles={onSendToSecureFiles} sendingToSecureFiles={sendingToSecureFiles} fileResource={attachmentResource} />,
  });

  return (
    <Paper
      variant="outlined"
      className={classNames(classes.root, {
        [classes.rootDesktop]: !isMobile,
        [classes.rootMobile]: isMobile,
      }, className)}
      role="button"
      test="thread-message-attachment"
      {...rootNodeProps}
    >
      <Grid
        container
        spacing={3}
        alignItems="center"
        className={classes.content}
        onClick={onClickOverride ? () => onClickOverride(attachmentResource) : () => onClick(attachmentResource)}
        test={`thread-message-attachment-${attachmentResource.link('self').url()}`}
      >
        <Grid
          item
          xs={isMobile ? 2 : 3}
          className={classes.fileIcon}
        >
          <FontAwesome
            icon={icon}
            style={{ color }}
            className={isMobile ? 'fa-lg' : 'fa-2x'}
          />
          <FontAwesome
            icon="arrow-circle-down"
            style={{ color }}
            className={classNames(isMobile ? 'fa' : 'fa-lg', classes.downloadIcon, {
              [classes.downloadIconDesktop]: !isMobile,
              [classes.downloadIconMobile]: isMobile,
            })}
          />
        </Grid>
        <Grid
          item
          className={classNames({
            [classes.labelBlockMobile]: isMobile,
          })}
          xs={isMobile ? 10 : 9}
        >
          <Typography
            className={classNames(classes.fileName, {
              [classes.fileNameMobile]: isMobile,
            })}
            noWrap
            variant="h5"
            test="thread-attachment-name"
          >
            {attachmentResource.attr('name')}
          </Typography>
          <Typography
            color="darkGray"
            variant="subtitle2"
            className={classNames(classes.downloadText, {
              [classes.labelFontMobile]: isMobile,
            })}
          >
            {'Click to view details'}
          </Typography>
          <Typography
            variant="subtitle2"
            color="darkGray"
            noWrap
            className={classNames(classes.fileLabel, {
              [classes.labelFontMobile]: isMobile,
            })}
            test="thread-attachment-label"
          >
            {label}
          </Typography>
        </Grid>
      </Grid>
      {onDelete ? (
        <IconButton
          className={classes.deleteIconButton}
          color="darkGray"
          title={`Delete ${attachmentResource.attr('name')}`}
          icon={FontAwesome}
          onClick={() => {
            onDelete(attachmentResource);
          }}
          IconProps={{
            icon: 'times-circle',
            className: classes.deleteIcon,
          }}
          test="remove-thread-attachment-button"
        />
      ) : null}
      {!isMobile && showHoverMenu && <>{ hoverMenu }</>}
    </Paper>
  );
};

MessageAttachment.defaultProps = {
  className: '',
  onDelete: null,
  onClickOverride: null,
  showHoverMenu: false,
};

MessageAttachment.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  attachmentResource: PropTypes.object.isRequired,
  onDelete: PropTypes.func,
  onClickOverride: PropTypes.func,
  showHoverMenu: PropTypes.bool,
};

export default withStyles(styles)(MessageAttachment);
